/**
 *  @file       passphrase-prompt.js
 *              Cross-platform passphrase prompt module for the Wallet API
 *
 *  @author     Wes Garland    - wes@kingsds.network
 *  @author     Ryan Rossiter  - ryan@kingsds.network
 *  @date       August 2019
 */
'use strict'; 

const debugging = require('dcp/debugging').scope(
  'dcp-client:wallet:passphrase-prompt',
);
const env = require('dcp/common/dcp-env');
const { setTimeout, clearTimeout } = require('dcp/common/dcp-timers');

const debug = (...args) => {
  if (debugging()) {
    args.unshift('dcp-client:wallet:passphrase-prompt');
    console.debug(...args);
  }
};

// This is the format for the username for password managers in the browser.
/** @param {Object|Keystore} metaData   Keystore-like object */
function generateUsername(metaData) {
  return (metaData.label || "Key") + " - " + (metaData.address || "Address");
}
exports.generateKeystorePasswordManagerUsername = generateUsername;

function ppServiceList() {
  const { readPass } = require('dcp/utils');

  let head = ppServiceList.pending[0]

  if (ppServiceList.pending.length === 0) {
    clearTimeout(ppServiceList.timer)
    ppServiceList.timer = undefined
    return
  }
  
  if (head.pending) {
    if (!ppServiceList.timer)
      ppServiceList.timer = setTimeout(ppServiceList, 100)
    return
  }
  
  head.pending = true
  function afterPrompt(ppPromise) {
    ppServiceList.timer = setTimeout(ppServiceList, 100)
    let idx = ppServiceList.pending.indexOf(head)
    if (idx !== -1)
      ppServiceList.pending.splice(idx, 1)
    head.resolve(ppPromise)
  }

  if (head.skipCheckFn && head.skipCheckFn())
    return afterPrompt()

  readPass(head.prompt).then(afterPrompt);
}
ppServiceList.pending = [];
ppServiceList.timer = null;

/**
 *  This function takes a string, prompt, prints it to the screen, and then
 *    returns whatever the user types after it.
 *
 * @param {Object} metaData Options
 * @param {string} metaData.label Label for the keystore
 * @param {*} urgent Read below, careful handling of event loop
 * @param {*} skipCheckFn see ppServiceList
 */
function wallet$$passphrasePrompt$node(prompt, urgent, skipCheckFn) {
  return new Promise(function(resolve, reject) {
    try {
      /* force the outputting of readPass() onto the event loop, so that pending 
        * console.log messages (etc) get printed first, when the loop is serviced,
        * instead of while we are typing the password.
        *
        * Also, make it so that it stalls on the event loop if there is already a
        * a password prompt being displayed.
        */
      let listO = {
        resolve: resolve,
        reject: reject,
        prompt,
        skipCheckFn: skipCheckFn,
      };

      if (ppServiceList.pending.length === 0 || urgent)
        setTimeout(ppServiceList, 0)
      if (urgent)
        ppServiceList.pending.unshift(listO)
      else
        ppServiceList.pending.push(listO)
    } catch(e) {
      reject(e);
    }
  });
}

async function promptCreatePassphrase(metaData={}) {
  if (env.platform === 'nodejs') {
    let pp1, pp2;
    do {
      pp1 = await require('../wallet').passphrasePrompt('Enter a passphrase for your Keystore:', metaData);
      pp2 = await require('../wallet').passphrasePrompt("Please confirm new passphrase by entering it again:", metaData, true);
    } while(pp1 !== pp2);

    return pp1;
  } else {
    const ClientModal = require('dcp/dcp-client/dom-tk').modals;
    let pp, tries = 0;
    function onBeforeShow(dialog)
    {
      if (tries++)
        dialog.querySelector('#dcp-modal-error').textContent = 'passphrases must match';
    }
    do
    {
      pp = await ClientModal.passwordPrompt({ username: generateUsername(metaData), mode: 'create', onBeforeShow });
    } while(pp);
    return pp;
  }
}

let createPassphraseFn = promptCreatePassphrase;
exports.promptCreatePassphrase = function wallet$$create$passphrase(...args) {
  debugging() && console.debug('wallet - promptCreatePassphrase call site ', new Error('debug\b\b\b\b\b\b\b\b\b\b\b\b            '));
  return createPassphraseFn(...args);
}

// only for testing:
exports.overrideCreatePassphrase = function wallet$$create$passphrase$override(fn) {
  createPassphraseFn = fn;
}

/** This function accepts arbitrary arguments and returns a string as provided by the user. 
 *  The default behaviour of this mode is in node, browser is a separate process in passphraseTries()
 *  
 * @param  {...any} args 
 * @return {Promise<string>} 
 */
exports.passphrasePrompt = async function passphrasePrompt(...args) {
  /**
   * This function was written out of spec, and over time has had a lot of additions to it that haven't been spec'd out.
   * To avoid breaking current applications that use the functionality included in this function, the function is overloaded to
   * process different kind of parameters.
   * 
   * If the first argument is a string, it will be used as the message to display when passphrasePrompt is called. 
   * metaData is an object containing the label of the keystore.
   * urgent is a boolean flag for ppServiceList() that works with the event loop.
   * tryPassphrase is a function (node implementation) and a string (browser implementation), which is used to skip input from user.
   * 
   */
  debug('passphrasePrompt args:', args);
  
  /**
   * Form 1:
   * passphrasePrompt(prompt, metaData, urgent/tryPassphrase)
   * Prompt is provided in the hook and urgent/tryPassphrase is set based on the value. Urgent can only be a boolean, so if not a boolean, it is tryPassphrase.
   * 
   * Form 2:
   * passphrasePrompt(metaData, tryPassphrase)
   * A default prompt is used
   */
  let prompt, metaData, urgent = false, tryPassphrase;
  if (typeof(args[0]) === 'string')
  {
    prompt = args[0];
    metaData = args[1];
    if (typeof(args[2]) === 'boolean')
      urgent = args[2]
    else
      tryPassphrase = args[2];
  }
  else
  {
    metaData = args[0];
    const ksIdentifier = metaData.label ? metaData.label : metaData.address;
    prompt = `Enter your passphrase here (${ksIdentifier}):`;
    if (args[1])
      tryPassphrase = args[1];
  }
    
  if (env.isBrowserPlatform) {
    const ClientModal = require('dcp/dcp-client/dom-tk').modals;
    await ClientModal.passwordPrompt(generateUsername(metaData), { prompt });
  } else {
    let skipCheckFn = tryPassphrase;
    let pp = await wallet$$passphrasePrompt$node(prompt, urgent, skipCheckFn);
    
    return pp;
  }
}


/** This function tries to get the correct passphrase
 *  by asking the user up to exports.maxPassphraseTries times.
 *  We give `passphrasePrompt` the callback `tryAndRememberPassphrase`
 *  so that the passwordCreation.js modal knows if a password was correct or not.
 *  This lets it trigger the browser to offer to save only correct passwords.
 *  If all tries are used and tryFn never returns truey, this function returns false.
 *
 *  @param {object}   metaData  metaData is passed to passphrasePrompt()
 *  @param {function} tryFn     function which trys to unlock the keystore with a supplied password
 */
exports.passphraseTries = async function wallet$$passphraseTries(metaData, tryFn, skipCheckFn) {
  let res;
  metaData.maxTries = exports.maxPassphraseTries

  const tryAndRememberPassphrase = async function keystore$$tryPassphrase(pp) {
    const thisRes = await tryFn(pp);
    res = thisRes || res;
    return !!thisRes;
  }

  // Tries using normal passphrase function, the user cannot override number of passphrase tries
  const ksIdentifier = metaData.label ? metaData.label : metaData.address;
  let prompt = `Enter your passphrase (${ksIdentifier}):`;
  let ppIsCorrect;
  for(let i = 0; !ppIsCorrect && i < metaData.maxTries; i++) {
    metaData.tries = i
    const pp = await require('../wallet').passphrasePrompt(prompt, metaData, skipCheckFn) // Just returns a passphrase
    ppIsCorrect = await tryAndRememberPassphrase(pp);
    prompt = `Incorrect passphrase, enter your passphrase again (${ksIdentifier}):`
  }
  return res || false;
}

exports.maxPassphraseTries = 5;
